import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CARDS_ES } from './cardsEs';

/**
 * Translate a card's display fields. The card keeps its id/filename/suit so
 * image lookups and shuffling logic stay untouched.
 */
export const localizeCard = (card, language) => {
  if (!card) return card;
  if (language !== 'es') return card;

  const translated = CARDS_ES[card.id];
  if (!translated) return card;

  return { ...card, name: translated.name, keywords: translated.keywords };
};

/**
 * Returns a stable `localize` function for translating cards inside components.
 */
export const useCardLocalizer = () => {
  const { i18n } = useTranslation();
  const { language } = i18n;

  return useCallback((card) => localizeCard(card, language), [language]);
};

/**
 * Translate a spread definition (name, description and every position label)
 * using the `spreads.<spread-id>` entries in the locale files.
 */
export const useLocalizedSpread = (spread) => {
  const { t } = useTranslation();

  if (!spread) return spread;

  return {
    ...spread,
    name: t(`spreads.${spread.id}.name`, { defaultValue: spread.name }),
    description: t(`spreads.${spread.id}.description`, { defaultValue: spread.description }),
    positions: spread.positions.map((position) => ({
      ...position,
      name: t(`spreads.${spread.id}.positions.${position.id}.name`, { defaultValue: position.name }),
      description: t(`spreads.${spread.id}.positions.${position.id}.description`, {
        defaultValue: position.description
      })
    }))
  };
};

/**
 * The list-friendly form of `useLocalizedSpread`: a stable callback, so a
 * component can translate every spread inside a `.map()` without calling a
 * hook in a loop.
 */
export const useSpreadLocalizer = () => {
  const { t } = useTranslation();

  return useCallback((spread) => {
    if (!spread) return spread;

    return {
      ...spread,
      name: t(`spreads.${spread.id}.name`, { defaultValue: spread.name }),
      description: t(`spreads.${spread.id}.description`, { defaultValue: spread.description }),
      positions: spread.positions.map((position) => ({
        ...position,
        name: t(`spreads.${spread.id}.positions.${position.id}.name`, { defaultValue: position.name }),
        description: t(`spreads.${spread.id}.positions.${position.id}.description`, {
          defaultValue: position.description
        })
      }))
    };
  }, [t]);
};
